"use client";
import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { CircleCheckBig, ChevronLeft, ChevronRight } from "lucide-react";
import { ICONS } from "@/lib/iconRegistry";
import GlobalTitle from "@shared/GlobalTitle";
import Link from "next/link";
import { Carousel, CarouselContent, CarouselItem } from "@ui/carousel";
import Autoplay from "embla-carousel-autoplay";

function SliderControls({ count, current, api }) {
    if (!api || count <= 1) return null;
    return (
        <div className="flex md:hidden items-center justify-center gap-4 mt-6">
            <button
                onClick={() => api.scrollPrev()}
                aria-label="Previous"
                className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
            >
                <ChevronLeft size={16} />
            </button>
            <div className="flex items-center">
                {Array.from({ length: count }).map((_, i) => (
                    <button
                        key={i}
                        onClick={() => api.scrollTo(i)}
                        aria-label={`Slide ${i + 1}`}
                        className="min-w-6 min-h-6 p-1.5 flex items-center justify-center cursor-pointer"
                    >
                        <span
                            className={`block h-1.5 rounded-full transition-all duration-300 ${
                                i === current ? "w-5 bg-primary" : "w-1.5 bg-border"
                            }`}
                        />
                    </button>
                ))}
            </div>
            <button
                onClick={() => api.scrollNext()}
                aria-label="Next"
                className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
            >
                <ChevronRight size={16} />
            </button>
        </div>
    );
}

function PillarCard({ pillar, index, total = 3, scrollYProgress, animate = true }) {
    const Icon = ICONS[pillar.icon];
    // Keep the stagger inside the scroll range so the last card still fully appears
    const step = Math.min(0.18, 0.5 / Math.max(total - 1, 1));
    const start = 0.1 + index * step;
    const end = start + 0.22;
    const y = useTransform(scrollYProgress, [start, end], [60, 0]);
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);

    return (
        <motion.div
            style={animate ? { y, opacity } : undefined}
            className={`relative h-full rounded-2xl border overflow-hidden ${pillar.bg} ${pillar.border}`}
        >
            <span className={`absolute top-2 right-5 text-4xl md:text-6xl font-black leading-none select-none pointer-events-none ${pillar.numColor}`}>
                {pillar.number}
            </span>

            <div className={`absolute -bottom-10 -left-10 w-44 h-44 rounded-full blur-3xl ${pillar.glow}`} />

            <div className="relative p-8 flex flex-col gap-5">
                <div className="flex flex-row items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center border backdrop-blur-md ${pillar.border} ${pillar.glow}`}>
                        <div className={`${pillar.iconColor} [&_svg]:w-7 [&_svg]:h-7`}>
                            {Icon && <Icon />}
                        </div>
                    </div>
                    <h3 className="text-xl font-bold text-foreground leading-snug pr-12">
                        {pillar.title}
                    </h3>
                </div>

                <div className={`h-px w-full ${pillar.divider}`} />

                <ul className="space-y-3">
                    {pillar.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 font-medium text-muted-foreground">
                            <CircleCheckBig size={15} className={`${pillar.check} mt-0.5 shrink-0`} strokeWidth={2} />
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </motion.div>
    );
}

export default function CostServices({ data: { topText, titleText, coloredText, description, comparisonTable, pillarsHeading, pillarsDescription, pillars = [], costFactors }, bg = "bg-[#f7f8fc]", }) {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start 80%", "end 60%"],
    });

    const [api, setApi] = useState(null);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (!api) return;
        const onSelect = () => setCurrent(api.selectedScrollSnap());
        onSelect();
        api.on("select", onSelect);
        api.on("reInit", onSelect);
        return () => api.off("select", onSelect);
    }, [api]);

    const mdBasis = pillars.length === 4 ? "md:basis-1/4" : "md:basis-1/3";
    // More cards than fit in a row (3 per row) → loop and auto-scroll through them
    const autoScroll = pillars.length > 4;
    const [autoplayPlugins] = useState(() => [Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]);

    return (
        <section ref={sectionRef} className={`relative ${bg} py-12 md:py-20 overflow-hidden`}>
            <div className="max-w-[1440px] mx-auto px-6">
                <GlobalTitle
                    topText={topText}
                    titleText={titleText}
                    coloredText={coloredText}
                    classes="text-center"
                    topTextClass="justify-center"
                />

                <p className="text-muted-foreground text-center max-w-4xl mx-auto mb-8">
                    {description}
                </p>

                {comparisonTable && (
                    <div className="max-w-4xl mx-auto mb-12">
                        {comparisonTable.title && (
                            <h3 className="text-xl font-bold text-foreground text-center mb-5">
                                {comparisonTable.title}
                            </h3>
                        )}
                        <div className="overflow-x-auto rounded-xl border border-border/60">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-muted/50">
                                        {comparisonTable.columns.map((col, i) => (
                                            <th key={i} className="text-left px-4 py-3 border border-border font-semibold">
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonTable.rows.map((row, i) => (
                                        <tr key={i} className={i % 2 === 1 ? "bg-muted/20" : ""}>
                                            {row.map((cell, j) => (
                                                <td key={j} className="px-4 py-3 border border-border">
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {comparisonTable.note && (
                            <p className="text-muted-foreground text-center max-w-3xl mx-auto mt-5">
                                {comparisonTable.note}
                            </p>
                        )}
                    </div>
                )}

                {(pillarsHeading || pillarsDescription) && (
                    <div className="text-center max-w-3xl mx-auto mb-8">
                        {pillarsHeading && (
                            <h3 className="text-xl font-bold text-foreground mb-2">{pillarsHeading}</h3>
                        )}
                        {pillarsDescription && (
                            <p className="text-muted-foreground">{pillarsDescription}</p>
                        )}
                    </div>
                )}

                {pillars.length > 0 && (
                <motion.div
                    initial={autoScroll ? { opacity: 0, y: 40 } : false}
                    whileInView={autoScroll ? { opacity: 1, y: 0 } : undefined}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                <Carousel
                    setApi={setApi}
                    opts={{ align: "start", loop: autoScroll, containScroll: "trimSnaps" }}
                    plugins={autoScroll ? autoplayPlugins : undefined}
                    className="w-full"
                >
                    <CarouselContent className="-ml-6 lg:-ml-8 items-stretch">
                        {pillars.map((pillar, i) => (
                            <CarouselItem
                                key={i}
                                className={`pl-6 lg:pl-8 basis-[85%] sm:basis-[65%] ${mdBasis}`}
                            >
                                <PillarCard
                                    pillar={pillar}
                                    index={i}
                                    total={pillars.length}
                                    // Auto-scrolling cards rotate into view at any time, so they skip the scroll-linked fade (the row fades in once instead)
                                    animate={!autoScroll}
                                    scrollYProgress={scrollYProgress}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
                </motion.div>
                )}
                <SliderControls count={pillars.length} current={current} api={api} />

                {costFactors?.length > 0 && (
                    <div className="mt-8 sm:mt-10 text-center">
                        <h3 className="text-base sm:text-lg font-bold text-foreground mb-3 sm:mb-4">Cost Factors</h3>
                        <div className="flex flex-wrap items-center justify-start md:justify-center gap-2 sm:gap-2.5">
                            {costFactors.map((factor, i) => (
                                <span
                                    key={i}
                                    className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-border bg-white text-xs sm:text-sm font-medium text-muted-foreground cursor-default transition-all duration-200 hover:border-primary hover:bg-primary/5 hover:text-primary hover:-translate-y-0.5"
                                >
                                    {factor}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <p className="text-center text-muted-foreground mt-8 text-sm md:text-base">
                    Not sure which plan fits your project?{" "}
                    <Link
                        href="/contact-us"
                        className="text-primary font-semibold underline underline-offset-4 hover:text-primary/80 transition-colors"
                    >
                        Enquire about cost
                    </Link>
                    {" "}and get a tailored estimate for your requirements.
                </p>
            </div>
        </section>
    );
}
